import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Loader2, Flame, Trophy, Target, ChevronDown, ChevronUp, MessageSquareText, HeartHandshake, MessageSquareQuote, Send } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—");
const Stat = ({ label, value, testid }) => (
  <div className="rounded-xl bg-slate-50 border border-slate-200/80 p-3" data-testid={testid}>
    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
    <div className="text-xl font-extrabold text-slate-900 font-display mt-0.5">{value}</div>
  </div>
);

function SkillBars({ skills }) {
  if (!skills.length) return <p className="text-sm text-slate-500">No scored conversations yet.</p>;
  return (
    <div className="space-y-2" data-testid="rep-skills">
      {skills.map((s) => (
        <div key={s.key} data-testid={`rep-skill-${s.key}`}>
          <div className="flex justify-between text-xs"><span className="font-medium text-slate-700">{s.skill}</span><span className="font-mono font-bold text-slate-900">{s.avg}</span></div>
          <div className="h-2 mt-1 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full rounded-full ${s.avg >= 70 ? "bg-emerald-500" : s.avg >= 45 ? "bg-cyan-500" : "bg-amber-500"}`} style={{ width: `${s.avg}%` }} /></div>
        </div>
      ))}
    </div>
  );
}

function NoteBox({ repId, session }) {
  const [note, setNote] = useState(session.manager_note || null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(!session.manager_note);
  const save = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { const r = await api.post(`/admin/reps/${repId}/sessions/${session.id}/note`, { text: text.trim() }); setNote(r.data.manager_note); setEditing(false); setText(""); toast.success("Note sent — they'll see it on their dashboard"); }
    catch { toast.error("Couldn't save the note"); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    try { await api.delete(`/admin/reps/${repId}/sessions/${session.id}/note`); setNote(null); setEditing(true); }
    catch { toast.error("Couldn't remove the note"); }
  };
  if (note && !editing) {
    return (
      <div className="mx-3.5 mb-3 rounded-xl bg-cyan-50 border border-cyan-200 p-3" data-testid={`rep-note-${session.id}`}>
        <div className="flex items-center justify-between gap-2 text-[10px] font-bold uppercase tracking-widest text-cyan-700">
          <span className="inline-flex items-center gap-1"><MessageSquareQuote className="w-3 h-3" /> Your note {note.read ? "· seen" : "· unread"}</span>
          <span className="flex gap-2 normal-case tracking-normal font-semibold">
            <button onClick={() => { setText(note.text); setEditing(true); }} className="hover:underline" data-testid={`rep-note-edit-${session.id}`}>Edit</button>
            <button onClick={remove} className="text-red-600 hover:underline" data-testid={`rep-note-delete-${session.id}`}>Remove</button>
          </span>
        </div>
        <p className="text-sm text-slate-800 mt-1">{note.text}</p>
      </div>
    );
  }
  return (
    <div className="mx-3.5 mb-3 flex gap-2" data-testid={`rep-note-form-${session.id}`}>
      <Textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={600} rows={2} placeholder="Quick coaching note for this call — they'll see it on their dashboard"
        className="flex-1 text-sm bg-slate-50 border-slate-200 min-h-[44px]" data-testid={`rep-note-input-${session.id}`} />
      <Button size="sm" onClick={save} disabled={busy || !text.trim()} className="self-end rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white" data-testid={`rep-note-send-${session.id}`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
      </Button>
    </div>
  );
}

function SessionCard({ s, repId }) {
  const [open, setOpen] = useState(false);
  const tone = s.rating >= 80 ? "text-emerald-700 bg-emerald-50 border-emerald-200" : s.rating >= 60 ? "text-amber-700 bg-amber-50 border-amber-200" : "text-red-700 bg-red-50 border-red-200";
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid={`rep-session-${s.id}`}>
      <div className="flex items-center gap-3 p-3.5">
        {s.persona_avatar && <img src={s.persona_avatar} alt="" className="w-10 h-10 rounded-xl object-cover object-top shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900 truncate">{s.persona_title} <span className="text-slate-400 font-normal">· {s.channel_label}</span></div>
          <div className="text-xs text-slate-500 truncate">{fmt(s.ended_at)} · {s.won ? "Deal won" : "Not closed"}{s.vulnerability_score != null && <span className="inline-flex items-center gap-1 ml-2 text-teal-700"><HeartHandshake className="w-3 h-3" /> Vulnerability {s.vulnerability_score}</span>}</div>
        </div>
        <span className={`px-2.5 py-1 rounded-full text-sm font-extrabold border ${tone}`}>{s.rating}</span>
      </div>
      {s.summary && <p className="px-3.5 pb-3 text-xs text-slate-600 leading-relaxed">{s.summary}</p>}
      {(s.missed_opportunities || []).length > 0 && (
        <ul className="px-3.5 pb-3 space-y-1">
          {s.missed_opportunities.slice(0, 2).map((m, i) => <li key={i} className="text-xs text-amber-800 flex gap-1.5"><span>→</span>{m}</li>)}
        </ul>
      )}
      <NoteBox repId={repId} session={s} />
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-emerald-700 py-2 border-t border-slate-100 bg-slate-50/60" data-testid={`rep-transcript-toggle-${s.id}`}>
        <MessageSquareText className="w-3.5 h-3.5" /> {open ? "Hide transcript" : `Read transcript (${s.transcript.length} turns)`} {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>
      {open && (
        <div className="px-3.5 py-3 space-y-2 bg-slate-50 max-h-80 overflow-y-auto" data-testid={`rep-transcript-${s.id}`}>
          {s.transcript.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] px-3 py-2 rounded-xl text-xs leading-relaxed ${m.role === "user" ? "bg-emerald-600 text-white" : "bg-white border border-slate-200 text-slate-700"}`}>
                <div className="text-[9px] font-mono uppercase tracking-wider opacity-70 mb-0.5">{m.role === "user" ? "Rep" : m.speaker || "Customer"}</div>
                {m.content}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function RepDetail({ repId, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!repId) return;
    setData(null); setError(null);
    api.get(`/admin/reps/${repId}`).then((r) => setData(r.data)).catch((e) => setError(e?.response?.data?.detail || "Couldn't load this rep"));
  }, [repId]);
  const rep = data?.rep;
  return (
    <Sheet open={!!repId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto bg-white" data-testid="rep-detail">
        {!data && !error && <div className="py-20 flex justify-center text-emerald-600"><Loader2 className="w-6 h-6 animate-spin" /></div>}
        {error && <p className="text-sm text-red-600 py-10" data-testid="rep-detail-error">{error}</p>}
        {rep && (
          <div className="space-y-6">
            <SheetHeader className="text-left">
              <SheetTitle className="text-2xl font-extrabold tracking-tight" data-testid="rep-detail-name">{rep.name}</SheetTitle>
              <SheetDescription>{rep.email} · Level {rep.level} · {rep.badges.length} badges · Last practised {fmt(rep.last_practiced_at)}</SheetDescription>
            </SheetHeader>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Stat label="Avg score" value={rep.avg_score} testid="rep-stat-avg" />
              <Stat label="Win rate" value={`${rep.win_rate}%`} testid="rep-stat-win" />
              <Stat label="Convos" value={rep.conversations} testid="rep-stat-convos" />
              <Stat label="Streak" value={<span className="inline-flex items-center gap-1"><Flame className="w-4 h-4 text-amber-500" />{rep.streak}</span>} testid="rep-stat-streak" />
            </div>
            <section>
              <h3 className="font-bold text-slate-900 flex items-center gap-2 mb-3"><Target className="w-4 h-4 text-red-500" /> Skills (last {data.recent.length} scored)</h3>
              <SkillBars skills={data.skills} />
            </section>
            {data.personas.length > 0 && (
              <section>
                <h3 className="font-bold text-slate-900 flex items-center gap-2 mb-3"><Trophy className="w-4 h-4 text-emerald-600" /> By customer type</h3>
                <div className="flex flex-wrap gap-2" data-testid="rep-personas">
                  {data.personas.map((p) => (
                    <span key={p.id} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${p.avg < 50 ? "bg-red-50 border-red-200 text-red-700" : "bg-white border-slate-200 text-slate-700"}`}>
                      {p.avatar && <img src={p.avatar} alt="" className="w-4 h-4 rounded-full object-cover" />}{p.title} <span className="font-mono">{p.avg}</span><span className="text-slate-400">×{p.attempts}</span>
                    </span>
                  ))}
                </div>
              </section>
            )}
            <section>
              <h3 className="font-bold text-slate-900 flex items-center gap-2 mb-3"><MessageSquareText className="w-4 h-4 text-cyan-600" /> Recent scorecards</h3>
              {data.recent.length === 0 ? <p className="text-sm text-slate-500" data-testid="rep-no-sessions">No completed conversations yet.</p>
                : <div className="space-y-3">{data.recent.map((s) => <SessionCard key={s.id} s={s} repId={repId} />)}</div>}
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
